import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import useScenePlayback from '../hooks/useScenePlayback';
import { Volume2, VolumeX, Timer, Hand } from 'lucide-react';
import { getLanguage } from '../i18n';
import GymIcon from './GymIcon';
import { getSceneImage } from '../services/SceneAssetRegistry';
import { unlockPracticeAudio } from '../services/soundEffects';
import './ScenePlayer.css';
import { t } from '../i18n';

function normalizeScene(scene, fallbackFrame = null) {
  const frames = Array.isArray(scene?.frames) && scene.frames.length > 0
    ? scene.frames
    : (fallbackFrame ? [fallbackFrame] : []);

  return {
    id: scene?.id || 'legacy-scene',
    playbackId: scene?.playbackId,
    frames: frames.map((frame) => ({
      id: frame?.id,
      audio: frame?.audio,
      audioUrl: frame?.audioUrl,
      subtitles: frame?.subtitles,
      name: frame?.name || t('practiceUi.scene.narrator'),
      text: frame?.text || frame?.comment || '',
      image: frame?.image || '',
      imageUrl: frame?.imageUrl || '',
      imageDisplay: frame?.imageDisplay === 'screen' ? 'screen' : 'character',
      alt: frame?.alt || frame?.name || t('practiceUi.scene.character'),
    })).filter((frame) => frame.text),
  };
}

function ScenePlayer({
  scene,
  fallbackFrame = null,
  onContinue,
  variant = 'coaching',
  continueLabel = t('common.continue'),
  nextLabel = t('common.next'),
  meta = null,
  showVs = false,
  messageTestId = 'scene-player-message',
  eventSpriteSrc = null,
  eventSpriteFrameCount = 8,
  eventSpriteFrameSize = 128,
  eventSpriteDurationMs = 720,
  embedded = false,
  fetchAudio,
  isPaused = false,
  captureGlobalKeys = true,
}) {
  const container = useRef(null);
  const normalizedScene = useMemo(() => normalizeScene(scene, fallbackFrame), [scene, fallbackFrame]);
  const [frameIndex, setFrameIndex] = useState(0);
  const [subtitleLanguage, setSubtitleLanguage] = useState(() => {
    try { const saved = localStorage.getItem('scene_subtitle_language'); if (saved === 'ko' || saved === 'en') return saved; } catch {}
    return getLanguage() === 'ko' ? 'ko' : 'en';
  });
  const completed = useRef(false);
  const stopPlayback = useRef(() => {});
  const [imageFailed, setImageFailed] = useState(false);
  const frame = normalizedScene.frames[frameIndex] || normalizedScene.frames[0] || { name: '', text: '', image: '' };
  const caption = frame.subtitles?.[subtitleLanguage];
  const resolvedImageSrc = frame.imageUrl || getSceneImage(frame.image);
  const imageSrc = imageFailed ? null : resolvedImageSrc;
  const imageDisplay = frame.imageDisplay === 'screen' ? 'screen' : 'character';
  const isLastFrame = frameIndex >= normalizedScene.frames.length - 1;

  useEffect(() => {
    setFrameIndex(0);
    completed.current = false;
  }, [normalizedScene.id]);

  useEffect(() => {
    setImageFailed(false);
  }, [resolvedImageSrc]);

  const handleAdvance = useCallback(() => {
    if (completed.current || isPaused) return;
    stopPlayback.current();
    void unlockPracticeAudio();
    if (!isLastFrame) {
      setFrameIndex((current) => Math.min(current + 1, normalizedScene.frames.length - 1));
      return;
    }

    if (onContinue) {
      completed.current = true;
      onContinue();
    }
  }, [isLastFrame, isPaused, normalizedScene.frames.length, onContinue]);
  const playback = useScenePlayback(normalizedScene, frameIndex, handleAdvance, fetchAudio, isPaused);
  stopPlayback.current = playback.stop;
  const ko = getLanguage() === 'ko';

  useEffect(() => {
    if (isPaused) return undefined;
    const handleKeyDown = (event) => {
      if ((embedded || !captureGlobalKeys) && !container.current?.contains(event.target)) return;
      if (!captureGlobalKeys && event.key === 'Escape') return;
      if (event.target?.closest?.('[data-scene-audio-control]') || event.key === 'Tab') return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      if (event.repeat) return;
      handleAdvance();
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [handleAdvance, embedded, isPaused, captureGlobalKeys]);

  return (
    <div
      ref={container}
      className={`scene-player scene-player-${variant}${embedded ? ' scene-player-embedded' : ''}`}
      role={embedded ? 'region' : 'dialog'}
      aria-modal={embedded ? undefined : 'true'}
      aria-hidden={isPaused || undefined}
      inert={isPaused ? '' : undefined}
      tabIndex={embedded ? 0 : undefined}
      aria-labelledby="scene-player-title"
      onClick={handleAdvance}
    >
      <div className="scene-player-stage">
        <div className="scene-player-toolbar" data-scene-audio-control onClick={event => event.stopPropagation()}>
          <div className="scene-player-language" role="group" aria-label={ko ? '자막 언어' : 'Subtitle language'}>
            {['ko', 'en'].map(language => <button key={language} type="button" aria-pressed={subtitleLanguage === language}
              disabled={!frame.subtitles?.[language]?.text}
              onClick={() => { setSubtitleLanguage(language); try { localStorage.setItem('scene_subtitle_language', language); } catch {} }}>
              {language === 'ko' ? '한국어' : 'English'}
            </button>)}
          </div>
          <button className="scene-player-round-control" type="button" aria-pressed={!playback.muted}
            aria-label={ko ? (playback.muted ? '음성 켜기' : '음성 끄기') : (playback.muted ? 'Unmute voice' : 'Mute voice')}
            onClick={playback.toggleMuted}>{playback.muted ? <VolumeX size={20} /> : <Volume2 size={20} />}</button>
          <button className="scene-player-round-control" type="button" aria-pressed={playback.autoAdvance}
            aria-label={ko ? (playback.autoAdvance ? '자동 넘기기 끄기' : '자동 넘기기 켜기') : (playback.autoAdvance ? 'Disable auto advance' : 'Enable auto advance')}
            onClick={playback.toggleAutoAdvance}>{playback.autoAdvance ? <Timer size={20} /> : <Hand size={20} />}</button>
        </div>
        {variant === 'opponent' && <div className="scene-player-flare" aria-hidden="true" />}
        {meta && <div className="scene-player-meta">{meta}</div>}

        <div className={`scene-player-character-wrap scene-player-character-wrap-${imageDisplay}`}>
          {showVs && <div className="scene-player-vs" aria-hidden="true">VS</div>}
          {imageSrc ? (
            <img
              className={`scene-player-character scene-player-character-${imageDisplay}`}
              src={imageSrc}
              alt={frame.alt || frame.name || t('practiceUi.scene.character')}
              onError={() => setImageFailed(true)}
            />
          ) : (
            <div className="scene-player-character-placeholder" aria-hidden="true" />
          )}
          {eventSpriteSrc && (
            <span
              className="scene-player-event-sprite"
              aria-hidden="true"
              style={{
                backgroundImage: `url(${eventSpriteSrc})`,
                width: `${eventSpriteFrameSize}px`,
                height: `${eventSpriteFrameSize}px`,
                backgroundSize: `${eventSpriteFrameSize * Math.max(eventSpriteFrameCount, 1)}px ${eventSpriteFrameSize}px`,
                animationDuration: `${eventSpriteDurationMs}ms`,
                animationTimingFunction: `steps(${Math.max(eventSpriteFrameCount, 1)})`,
                '--scene-player-event-sprite-end-x': `-${eventSpriteFrameSize * Math.max(eventSpriteFrameCount, 1)}px`,
              }}
            />
          )}
        </div>

        <div className="scene-player-dialogue">
          <div className="scene-player-speaker" id="scene-player-title">
            {caption?.name || frame.name}
          </div>
          <p className="scene-player-message" data-testid={messageTestId}>
            {caption?.text || frame.text}
          </p>
          <div className="scene-player-audio-controls" data-scene-audio-control onClick={event => event.stopPropagation()}>
            {playback.blocked && <button type="button" onClick={playback.retry}>{ko ? '탭하여 재생' : 'Tap to play'}</button>}
          </div>
          <button
            className="scene-player-continue"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              handleAdvance();
            }}
          >
            <GymIcon className="scene-player-continue-icon" />
            <span>{isLastFrame ? continueLabel : nextLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScenePlayer;
